import router from '@/router';

import { type RouterGuard, type GuardContext, createRouterGuard } from '@/lib/tellscale/router-guard';

import { useRecommendationPortal } from "@/stores/portal";
import { useAuth } from "@/stores/auth";

/**
 * Register all router guard redirect rules
 *
 * @param guard - RouterGuard instance for adding redirect rules
 * @param context - Route context containing to, from, and query
 */
const registerGuardRules = (guard: RouterGuard, context: GuardContext): void => {
    const portal = useRecommendationPortal();
    const auth = useAuth();

    const { query } = context;

    // Redirect to an invalid portal page if the portal is not loaded
    guard.addRedirectRule({ path: '/portal/invalid', query })
        .checkCondition(() => !portal.isLoaded);

    // Redirect to inactive portal page if portal is inactive
    guard.addRedirectRule({ path: '/portal/inactive', query })
        .checkCondition(() => portal.isInactive);

    // Redirect unauthenticated users to login (default behavior for routes without auth or with auth: 'authenticated')
    guard.addRedirectRule({ path: '/portal/login', query })
        .checkMeta('auth', 'authenticated', true)
        .checkCondition(() => !auth.isAuthenticated);

    // Redirect users to login if auth meta is unauthenticated and the portal is loaded and not inactive
    guard.addRedirectRule({ path: '/portal/login', query })
        .checkMeta('auth', 'unauthenticated')
        .checkCondition(() => portal.isLoaded && !portal.isInactive)
        .checkCondition(() => !auth.loginFlowData)
        .checkCondition(() => !context.to.path.startsWith('/auth/failure'));

    // Redirect authenticated users away from unauthenticated-only pages
    guard.addRedirectRule({ path: '/portal/dashboard', query })
        .checkMeta('auth', 'unauthenticated')
        .checkCondition(() => auth.isAuthenticated && portal.isLoaded && !portal.isInactive);

    // Redirect authenticated users to the dashboard if they are on the login success route
    guard.addRedirectRule({ path: '/portal/dashboard', query })
        .checkCondition(() => portal.isLoaded && !portal.isInactive)
        .checkCondition(() => auth.isAuthenticated && !auth.loginFlowData)
        .checkCondition(() => context.to.path === '/auth/success');
}

const routerGuard = createRouterGuard({
    guardRules: registerGuardRules,
    router: router,
});

export default routerGuard;